import { React, useState } from 'react'
import { Link, useHistory } from 'react-router-dom'
import { DateTime } from 'luxon'
import Button from '@material-ui/core/Button'
import Badge from '@material-ui/core/Badge'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemText from '@material-ui/core/ListItemText'
import TableRow from '@material-ui/core/TableRow'
import { Menu, MenuItem, Box, Avatar } from '@material-ui/core'
import DeleteLeadDialog from 'components/delete-lead-dialog'
import MarkAsContactedDialog from './mark-as-contacted-dialog'
import PersonOutlineIcon from '@material-ui/icons/PersonOutline'
import PersonAddOutlinedIcon from '@material-ui/icons/PersonAddOutlined'
import DeleteIcon from '@material-ui/icons/DeleteOutlined'
import { makeStyles } from '@material-ui/styles'
import TableCell from '@material-ui/core/TableCell'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import CoachAddStudentModal from 'components/add-student-modal/coach-add-student-modal'
import { useIsTablet } from 'lib/hooks'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import SensitiveInfoWrapper from 'lib/auth/SensitiveInfoWrapper'
import MessageOutlinedIcon from '@material-ui/icons/MessageOutlined'
import CheckIcon from '@material-ui/icons/Check'
import StreamSendMessageButton from 'components/stream-messaging/send-message-button'
import { amplitudeEvent } from 'lib/amplitude'
import useMediaQuery from '@material-ui/core/useMediaQuery'

export const useStyles = makeStyles(theme => ({
  leadRow: {
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  leadDetailsLink: {
    textDecoration: 'none',
    color: theme.palette.text.primary,
  },
  leadText: {
    color: theme.palette.text.primary,
  },
  unviewedLeadText: {
    color: theme.palette.error.dark,
  },
  leadNameCell: {
    borderTop: '1px solid #EEEEEE !important',
    borderLeft: '1px solid #EEEEEE !important',
    borderBottom: '1px solid #EEEEEE !important',
    borderRadius: '8px 0 0 8px!important',
    paddingLeft: '1.5rem',
  },
  row: {
    cursor: 'pointer',
  },
  info: {
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    [theme.breakpoints.down('sm')]: {
      whiteSpace: 'normal',
      overflow: 'visible',
      textOverflow: 'clip',
    },
  },
  studentAvatar: {
    height: '36px',
    width: '36px',
  },
  text: {
    fontSize: '16px',
    fontWeight: 400,
    lineHeight: '24px',
    letterSpacing: '0.15px',
    textAlign: 'left',
  },
  actions: {
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  link: {
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  icon: {
    color: theme.palette.grey[600],
    fontSize: '1.25rem',
  },
  badge: {
    left: '-0.75rem',
    top: '50%',
    backgroundColor: theme.palette.blue.badge,
  },
}))

export const LeadTableItem = ({
  lead,
  contactMethodOptions = [],
  refetch,
  studentRefetch,
  showRespondButton = false,
  showRelativeTime = false,
}) => {
  const classes = useStyles()
  const isTablet = useIsTablet()
  const isDesktop = useMediaQuery('(min-width: 1200px)')
  const history = useHistory()
  const timeToShowWarning = 1000 * 60 * 60 * 24 * 7 // 1 week

  const { contact } = lead

  const [anchorEl, setAnchorEl] = useState(null)

  const [
    isConvertLeadToStudentModalOpen,
    setIsConvertLeadToStudentModalOpen,
  ] = useState(false)

  const [isDeleteLeadDialogOpen, setIsDeleteLeadDialogOpen] = useState(false)

  const [
    isMarkAsContactedDialogOpen,
    setIsMarkAsContactedDialogOpen,
  ] = useState(false)

  const handleMoreVertClick = event => {
    event.stopPropagation()
    setAnchorEl(event.currentTarget)
  }

  const handleClose = event => {
    event.stopPropagation()
    setAnchorEl(null)
  }

  const convertLeadToStudentButton = event => {
    event.stopPropagation()
    amplitudeEvent('convert-to-student-clicked', {
      leadId: lead.id,
      url: window.location.href,
      path: window.location.pathname,
    })
    setIsConvertLeadToStudentModalOpen(true)
  }

  const openDeleteLeadDialog = event => {
    event.stopPropagation()
    setIsDeleteLeadDialogOpen(true)
  }

  const openMarkAsContactedDialog = event => {
    event.stopPropagation()
    setIsMarkAsContactedDialogOpen(true)
  }

  const closeMarkAsContactedDialog = () => {
    setIsMarkAsContactedDialogOpen(false)
  }

  const refetchLeadsAndStudents = () => {
    refetch()
    studentRefetch()
  }

  const styleLeads = () => {
    let str = ''
    const submittedTime = DateTime.fromISO(lead.createdAt).toMillis()
    const now = DateTime.now().toMillis()
    if (!lead.contactedAt && now - submittedTime > timeToShowWarning) {
      str += `${classes.unviewedLeadText}`
    }
    return str
  }

  return (
    <>
      <TableRow
        className={classes.row}
        onClick={() => history.push(`/pga-coach/leads/${lead.id}`)}
      >
        <TableCell className={classes.info}>
          <List disablePadding={true}>
            <Link
              to={`/pga-coach/leads/${lead.id}`}
              className={classes.leadDetailsLink}
            >
              <ListItem>
                <Badge
                  variant="dot"
                  badgeContent={!lead.viewedAt ? ' ' : 0}
                  anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                  }}
                  classes={{ badge: classes.badge }}
                >
                  <ListItemIcon>
                    <Avatar
                      variant="circular"
                      className={classes.studentAvatar}
                    >
                      {contact.firstName[0]}
                      {contact.lastName[0]}
                    </Avatar>
                  </ListItemIcon>
                </Badge>
                <Box pl={2}>
                  {isTablet && (
                    <ListItemText className={styleLeads()}>
                      Submitted:{' '}
                      {showRelativeTime
                        ? DateTime.fromISO(lead.createdAt).toRelative()
                        : DateTime.fromISO(lead.createdAt).toLocaleString(
                          DateTime.DATE_MED,
                        )}
                    </ListItemText>
                  )}
                  <ListItemText>
                    {contact.firstName} {contact.lastName}
                  </ListItemText>
                </Box>
              </ListItem>
            </Link>
          </List>
        </TableCell>
        {!isTablet && (
          <TableCell className={styleLeads()}>
            {showRelativeTime
              ? DateTime.fromISO(lead.createdAt).toRelative()
              : DateTime.fromISO(lead.createdAt).toLocaleString(
                DateTime.DATE_MED,
              )}
          </TableCell>
        )}
        {!isTablet && (
          <TableCell>
            {lead.intent.replace(/\s,/g, '').replace(/^,\s/g, '')}
          </TableCell>
        )}
        <TableCell className={classes.actions}>
          {showRespondButton && (
            <StreamSendMessageButton
              userIds={[lead.contact.externalId]}
              variant="contained"
              color="primary"
              size="small"
              style={{ marginRight: 8 }}
              startIcon={<MessageOutlinedIcon />}
            >
              Respond
            </StreamSendMessageButton>
          )}
          {contactMethodOptions.length > 0 && isDesktop && (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              startIcon={<CheckIcon />}
              onClick={openMarkAsContactedDialog}
            >
              Mark as Contacted
            </Button>
          )}
          <Button
            aria-controls="simple-menu"
            aria-haspopup="true"
            onClick={handleMoreVertClick}
            data-testid="lead-details-actions"
            size="small"
            style={{ minWidth: '40px' }}
          >
            <MoreVertIcon fontSize="small" />
          </Button>
        </TableCell>
      </TableRow>
      <Menu
        id="simple-menu"
        keepMounted
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
      >
        <Link
          to={`/pga-coach/leads/${lead.id}`}
          className={classes.leadDetailsLink}
        >
          <MenuItem>
            <ListItemIcon>
              <PersonOutlineIcon className={classes.icon} />
            </ListItemIcon>
            <ListItemText primary="View Lead Details" />
          </MenuItem>
        </Link>
        <MenuItem onClick={convertLeadToStudentButton}>
          <ListItemIcon>
            <PersonAddOutlinedIcon className={classes.icon} />
          </ListItemIcon>
          <ListItemText primary="Convert to Student" />
        </MenuItem>
        {!isDesktop && contactMethodOptions.length > 0 && (
          <MenuItem onClick={openMarkAsContactedDialog}>
            <ListItemIcon>
              <CheckIcon className={classes.icon} />
            </ListItemIcon>
            <ListItemText primary="Mark as Contacted" />
          </MenuItem>
        )}
        <SensitiveInfoWrapper>
          <MenuItem onClick={openDeleteLeadDialog} data-testid="delete-lead">
            <ListItemIcon>
              <DeleteIcon className={classes.icon} />
            </ListItemIcon>
            <ListItemText primary="Delete Lead" />
          </MenuItem>
        </SensitiveInfoWrapper>
      </Menu>
      <CoachAddStudentModal
        open={isConvertLeadToStudentModalOpen}
        setOpen={setIsConvertLeadToStudentModalOpen}
        onAdd={refetchLeadsAndStudents}
        initialValues={{
          studentFirstName: lead.firstName,
          studentLastName: lead.lastName,
          email: lead.contact?.email,
          phoneNumber: lead.contact?.phone,
        }}
        leadId={lead.id}
      />
      <DeleteLeadDialog
        lead={lead}
        open={isDeleteLeadDialogOpen}
        setOpen={setIsDeleteLeadDialogOpen}
      />
      <MarkAsContactedDialog
        open={isMarkAsContactedDialogOpen}
        onClose={closeMarkAsContactedDialog}
        contactMethodOptions={contactMethodOptions}
        lead={lead}
        refetch={refetch}
      />
    </>
  )
}
